import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { PercentageIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import {
  getMissionTrackingTotalSalariesWithoutPas,
  getMissionTrackingTotalSalariesWithPas
} from '../../api/mission-trackings/get-mission-tracking-salary-total'
import { updateMissionTrackingPas } from '../../api/mission-trackings/update-mission-tracking-pas'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { getMissionTrackingSalaryColumns } from '../../components/pages/mission-trackings/mission-tracking-salary.utils'
import { MissionTrackingSalaryFilters } from '../../components/pages/mission-trackings/mission-tracking-salary-filters'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import {
  MissionToStaffSalaryFilter,
  MissionTrackingSalary
} from '../../lib/types/models/mission-tracking/mission-tracking'
import {
  transformMissionToStaffSalaryFiltersToUrl,
  transformUrlToMissionToStaffSalaryFilters
} from '../../lib/utils/filters-mapping/mission-to-staff-salary'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

export function AccountantMissionTrackingSalariesListPage() {
  const { t } = useTranslate()
  const tableRef = useRef<DynamicTableRef<MissionTrackingSalary>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<MissionToStaffSalaryFilter>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformMissionToStaffSalaryFiltersToUrl,
      transformUrlToFilter: transformUrlToMissionToStaffSalaryFilters
    })

  const { mutateAsync: updateMissionTrackingPasMutation } = useMutation({
    mutationFn: updateMissionTrackingPas,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Le PAS a été mis à jour avec succès')
      tableRef.current?.refetch()
    }
  })

  const { data: totalSalariesWithPas } = useQuery({
    queryKey: ['mission-tracking-salaries-total-with-pas', JSON.stringify(selectedFilter)],
    queryFn: () => getMissionTrackingTotalSalariesWithPas(selectedFilter ?? {})
  })

  const { data: totalSalariesWithoutPas } = useQuery({
    queryKey: ['mission-tracking-salaries-total-without-pas', JSON.stringify(selectedFilter)],
    queryFn: () => getMissionTrackingTotalSalariesWithoutPas(selectedFilter ?? {})
  })

  return (
    <div>
      <UserNavBar title={'Saisie du PAS (Honoraires AAT)'} icon={PercentageIcon} />
      <div className="h-4" />

      <DynamicDataTable<MissionTrackingSalary, MissionToStaffSalaryFilter>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        title={'Saisie du PAS'}
        disableRow={({ pasBlocked }) => !pasBlocked}
        columns={getMissionTrackingSalaryColumns({
          t,
          role: RoleEnum.ACCOUNTANT,
          handlePasChange: async (amount, missionTracking) =>
            await updateMissionTrackingPasMutation({ id: missionTracking.id, amount })
        })}
        allowSearchParam
        specialRows={[
          { color: tailwindColors.grey.light.DEFAULT, condition: ({ pasBlocked }) => pasBlocked }
        ]}
        onFiltersClick={() => toggleModal('filters')}
        endpoint={API.missionTrackings.getMissionTrackingsSalaries.url}
        permissions={{ delete: false }}
        footerclassName="bg-primary-light"
        footerCells={[
          {
            accessorKey: 'id',
            value: (
              <Typography variant="caption" color={tailwindColors.primary.DEFAULT}>
                Total
              </Typography>
            )
          },
          {
            accessorKey: 'viewData.totalSalary',
            value: (
              <Badge
                variant="orange"
                text={NumberFormatter.formatCurrency(totalSalariesWithoutPas?.total ?? 0)}
              />
            )
          },
          {
            accessorKey: 'viewData.totalSalaryAfterPas',
            value: (
              <Badge
                variant="orange"
                text={NumberFormatter.formatCurrency(totalSalariesWithPas?.total ?? 0)}
              />
            )
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <MissionTrackingSalaryFilters filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
